import React, {useEffect, useMemo, useState} from 'react';
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {useDispatch, useSelector} from "react-redux";
import orderBy from "lodash-es/orderBy";
import {sortAlphabetically} from "@arboxappv4/shared/src/helpers/functions";
import {
    EllipsisText,
    FlexColumnSection,
    FlexRowSection,
    FlexSection,
    SeparatorVerticalDiv,
    SmallHeader,
    SmallText
} from "../../../styles/globalStyles";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {Spin} from "antd";
import {t} from "i18next";
import {
    addFiltersToQueryParams,
    getMembershipAddons,
    getMembershipPriceText,
    getSeriesEventsDaysText,
    selectTrackMembershipType,
    courseBookingAllowed,
    getAttendanceDisplay,
} from "../../../helpers/functions";
import CustomCollapse from "../../../components/UI/CustomCollapse";
import dayjs from "dayjs";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {updateFlags} from "@arboxappv4/shared/src/redux/stepperSlice";
import SingleItemPage from "../SingleItemPage";
import TracksHeader from "./TracksHeader";
import {useNavigate, useSearchParams} from 'react-router-dom';
import {DB_DATE_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {getDisabledPagesCourseProps} from "@arboxappv4/shared/src/helpers/DisablePages";

const Tracks = (props) => {
    const { handleSubmit, values, setFieldValue, isDirectTrack } = props;
    const globalLocation = useSelector(state => state.site.selectedLocation)
    const box = useSelector(state => state.site.box)
    const loggedIn = useSelector(state => state.auth.loggedIn)
    const user = useSelector(state => state.user.data)
    const flags = useSelector(state => state.stepper.flags)
    const activeUsersBoxes = useSelector(state => state.user.activeUsersBoxes)

    const [tracks, setTracks] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isEmpty, setIsEmpty] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [filteredTracks, setFilteredTracks] = useState(null);

    const [searchParams] = useSearchParams();
    const currencySymbol = useMemo(() => globalLocation?.currency_symbol, [globalLocation]);
    const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
    const {isMobile} = useScreenSize()
    const dispatch = useDispatch()
    const navigate = useNavigate();

    useEffect(() => {
        if(globalLocation) {
            if(params.allLocations === 'true') {
                setSelectedLocation('all')
            } else if(loggedIn && activeUsersBoxes?.locations_box_fk) {
                setSelectedLocation(activeUsersBoxes.locations_box_fk)
            } else {
                setSelectedLocation(globalLocation?.id)
            }
        }
    }, [globalLocation, loggedIn, activeUsersBoxes]);

    useEffect(() => {
        if(selectedLocation) {
            fetchTracks()
            if (selectedLocation !== 'all') addFiltersToQueryParams(navigate, {location: selectedLocation, allLocations: false})
            else addFiltersToQueryParams(navigate, {allLocations: true})
        }
    }, [selectedLocation, user]);

    useEffect(() => {
        if(values.serviceId && flags.serviceReady) {
            dispatch(updateFlags({serviceReady: false}))
            handleSubmit()
        }
    }, [values.serviceId, flags.serviceReady]);

    useEffect(() => {
        if(filteredTracks) {
            setIsEmpty(filteredTracks.length === 0)
        }
    }, [filteredTracks]);

    const fetchTracks = async () => {
        setFetching(true)
        setFilteredTracks(null)
        const tracksRes = await apiAction(`getTracks`, 'post', {location_id: selectedLocation === 'all' ? null : selectedLocation, user_id: user ? user.id : null});
        handleTracksResult(tracksRes ?? [])
        setFetching(false)
    }

    const handleTracksResult = (tracksRes) => {
        setIsEmpty(tracksRes.length === 0)
        const sorted = sortAlphabetically(tracksRes, 'name');
        setTracks(sorted)
        setFilteredTracks(sorted)
    }

    const getTrackHeaderInfo = (track) => {
        const trackEvents = (track?.series_track_relations ?? []).flatMap(relation => relation?.series?.series_events ?? [])
        const daysText = getSeriesEventsDaysText(trackEvents, {dedupeByDay: true})
        const frequencyText = t('frequency-track-label', {frequency: track.session_per_week})

        return (
            <div style={{width: isMobile ? '100%' : 'auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '20px', alignItems: isMobile ? 'start' : 'center'}}>
                <EllipsisText isMobile={isMobile} width={'120px'}>{getMembershipPriceText(track.membership_types[0], currencySymbol)}</EllipsisText>
                {daysText && <EllipsisText isMobile={isMobile} width={'200px'}>{daysText}</EllipsisText>}
                <EllipsisText isMobile={isMobile} width={'150px'}>{frequencyText}</EllipsisText>
                {selectedLocation === 'all' && <EllipsisText isMobile={isMobile} width={loggedIn ? '50%' : '100px'}>{track.locations_box?.location}</EllipsisText>}
            </div>
        )
    }

    const getCollapseInfo = (track) => {
        const sortedRelations = orderBy(track.series_track_relations ?? [], [relation => relation?.series?.series_name ?? ''])
        const membershipType = selectTrackMembershipType(track)

        return (
            <FlexColumnSection gap={isMobile ? '8px' : '15px'}>
                <FlexSection gap={isMobile ? '8px' : '15px'} flex={'unset'} mobile={isMobile} align={'start'}>
                    <FlexColumnSection flex={'unset'}>
                        <SmallText weight={'bold'}>{t('courses-included-in-track')}</SmallText>
                        <FlexColumnSection flex={'unset'}>
                            {sortedRelations.map(relation => {
                                const series = relation.series
                                if (!series) return null
                                const daysText = getSeriesEventsDaysText(series?.series_events)
                                const displayProps =  getDisabledPagesCourseProps(series)
                                const attendanceDisplay = getAttendanceDisplay(displayProps, series)
                                return (
                                    <FlexRowSection key={`track-course-${series.id}`} gap={'8px'} align={'center'}>
                                        <span>{`${series.series_name}${daysText ? ` - ${daysText}` : ''}`}</span>
                                        {((displayProps.showNumRegistered || displayProps.showNumOpenSpots) && !series.isHugFull) &&
                                        <EllipsisText isMobile={isMobile} width={'80px'}>
                                            <FlexRowSection gap={'8px'}>
                                                <FontAwesomeIcon icon={solid('user')} color={Colors.siteBlack} size={'xs'}/>
                                                <span>{attendanceDisplay}</span>
                                            </FlexRowSection>
                                        </EllipsisText>}
                                    </FlexRowSection>
                                )
                            })}
                        </FlexColumnSection>
                    </FlexColumnSection>
                    <SeparatorVerticalDiv color={Colors.greyText} height={'100%'} margin={'0 10px'}/>
                    <div style={{width: '320px'}}>
                        <FlexColumnSection flex={'unset'}>
                            {track.bio &&
                                <div>
                                    <SmallText weight={'bold'}>{t('description')}:</SmallText>
                                    <div style={{flex: 1, whiteSpace: 'pre-wrap'}}>{track.bio}</div>
                                </div>
                            }
                            {(track.start_date || track.end_date) &&
                                <FlexColumnSection flex={'unset'} style={{paddingTop: track.bio ? '10px' : 0}}>
                                    <SmallText weight={'bold'}>{t('track-days-header')}</SmallText>
                                    {track.start_date && <div>{t('starts-at', {start: dayjs(track.start_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})}</div>}
                                    {track.end_date && <div>{t('ends-at', {end: dayjs(track.end_date, DB_DATE_FORMAT).format(globalLocation.date_format), interpolation: {'escapeValue': false}})}</div>}
                                    {(track.allow_mid_booking && track.has_started) ? <div>{t('track-started-expand-label')}</div> : null}
                                </FlexColumnSection>
                            }
                        </FlexColumnSection>
                    </div>
                    <SeparatorVerticalDiv color={Colors.greyText} height={'100%'} margin={'0 10px'}/>
                    <FlexColumnSection>
                        <SmallText weight={'bold'}>{t('price')}:</SmallText>
                        {track.membership_types?.map(mt => (
                            <div key={`track-mt-${mt.id}`}>
                                {getMembershipPriceText(mt, currencySymbol)} {mt.addons?.length ? t(`for-hug-${mt.type}`) : ''}
                            </div>
                        ))}
                        {membershipType?.addons?.length > 0 && <div>{getMembershipAddons(membershipType.addons, currencySymbol)}</div>}
                        {membershipType?.max_installments > 1 && <div>{t('max-installments', {max_installments: membershipType.max_installments})}</div>}
                        {track.register_group_member ? t('register-group-member-tracks-label') : null}
                    </FlexColumnSection>
                </FlexSection>
            </FlexColumnSection>
        )
    }

    const onTrackSelected = (membershipType, track) => {
        if(!membershipType) return
        setFieldValue('serviceId', membershipType.id)
        setFieldValue('locations_box_fk', track.locations_box_id)
        dispatch(updateFlags({
            serviceReady: true,
            trackInfo: track,
            isChildRegistration: !!track.register_group_member,
            membershipTypeSelection: membershipType
        }))
    }

    const onTrackClick = (track, event = null) => {
        if(event) event.stopPropagation();
        setFieldValue('track', track)
        onTrackSelected(selectTrackMembershipType(track), track)
    }

    const getTrackCtaText = (track) => {
        if(track.isBookedUser) return t('user-booked-to-course')
        if(track.isTrackFull) return t('track_full')
        return t('book')
    }

    const onFilterChange = (filteredTracksList, filtersConfig) => {
        if(filtersConfig?.locations) {
            setSelectedLocation(filtersConfig?.locations.selected ?? null)
        }
        setFilteredTracks(filteredTracksList)
    }

    return (
        <>
            {isDirectTrack ?
                <SingleItemPage values={values} setFieldValue={setFieldValue} handleSubmit={handleSubmit} params={{...values, onCtaClick: () => onTrackClick(values.track)}}/>
                :
                <FlexColumnSection gap={'15px'} padding={isMobile ? '0 20px' : 0}>
                    <TracksHeader tracks={tracks}
                                  onFiltered={onFilterChange}
                                  selectedLocation={selectedLocation}
                    />
                    <Spin spinning={fetching}/>
                    <FlexColumnSection gap={'10px'} overflow={'hidden auto'}>
                        {!fetching && isEmpty && <span>{t('no-tracks-found')}</span>}
                        {!fetching && filteredTracks?.map(track => {
                            const disabled = track.isTrackFull || track.isBookedUser || !courseBookingAllowed(activeUsersBoxes, track)
                            return (
                                <CustomCollapse headerText={track.name}
                                                headerFontSize={'16px'}
                                                ctaText={getTrackCtaText(track)}
                                                headerWidth={'40%'}
                                                headerInfo={getTrackHeaderInfo(track)}
                                                onBtnClick={(event) => onTrackClick(track, event)}
                                                key={`track-panel-${track.id}`}
                                                restStyles={isMobile ? '' : 'width: 100px;'}
                                                disableExpand={disabled ? 'disabled' : null}
                                                ctaWidth={disabled ? 'auto' : '100px'}
                                >
                                    {getCollapseInfo(track)}
                                </CustomCollapse>
                            )
                        })}
                    </FlexColumnSection>
                </FlexColumnSection>
            }
        </>
    );
};

export default Tracks;
